import React, { useState, useRef, useCallback } from 'react';
import { usePlatformAuth } from '../contexts/AuthContext';
import { DocumentContent, Account } from '../types';
import { api } from '../services/api';
import { LoadingOverlay } from './LoadingOverlay';

// PDF.js types
declare global {
  interface Window {
    pdfjsLib: any;
  }
}

interface TemplateUploadModalProps {
  isVisible: boolean;
  onClose: () => void;
  onTemplateAdded: () => void;
  currentAccount: Account | null;
  onAccountUpdated?: (account: Account) => void; // add this line
}

export const TemplateUploadModal: React.FC<TemplateUploadModalProps> = ({
  isVisible,
  onClose,
  onTemplateAdded,
  currentAccount,
}) => {
  const auth = usePlatformAuth();
  const [uploadedFile, setUploadedFile] = useState<DocumentContent | null>(null);
  const [isValidating, setIsValidating] = useState(false);
  const [validationResult, setValidationResult] = useState<{ success: boolean; message: string } | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState<{ success: boolean; message: string } | null>(null);
  const [templateName, setTemplateName] = useState<string>('');
  const [, setFieldCount] = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const convertFileToDocumentContent = (file: File): Promise<DocumentContent> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = (reader.result as string).split(',')[1];
        resolve({
          name: file.name || `template_${Date.now()}.pdf`,
          format: 'pdf',
          content: base64,
        });
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const handleFileSelect = useCallback(async (files: FileList | File[]) => {
    const pdfFiles = Array.from(files).filter(file => 
      file.type === 'application/pdf'
    );

    if (pdfFiles.length === 0) {
      alert('Please select PDF files only.');
      return;
    }

    if (pdfFiles.length > 1) {
      alert('Please select only one PDF file.');
      return;
    }

    try {
      const document = await convertFileToDocumentContent(pdfFiles[0]);
      setUploadedFile(document);
      setValidationResult(null);
      setUploadResult(null);
    } catch (error) {
      console.error('Error processing file:', error);
      alert('Error processing file. Please try again.');
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    handleFileSelect(e.dataTransfer.files);
  }, [handleFileSelect]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
  }, []);

  const handleFileInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      handleFileSelect(e.target.files);
    }
  }, [handleFileSelect]);

  const handleClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const convertBase64ToFile = useCallback((documentContent: DocumentContent): Promise<File> => {
    return new Promise((resolve) => {
      const byteCharacters = atob(documentContent.content);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'application/pdf' });
      const file = new File([blob], documentContent.name, { type: 'application/pdf' });
      resolve(file);
    });
  }, []);

  const extractPdfFields = useCallback(async (file: File): Promise<{ fields: Array<{ page: number, name: string, type: string }>, jsonSchema: string }> => {
    // Load PDF.js if not already loaded
    if (!window.pdfjsLib) {
      const script = document.createElement('script');
      script.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/2.16.105/pdf.min.js';
      await new Promise((resolve, reject) => {
        script.onload = resolve;
        script.onerror = reject;
        document.head.appendChild(script);
      });
      
      // Set worker source
      window.pdfjsLib.GlobalWorkerOptions.workerSrc = 
        'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/2.16.105/pdf.worker.min.js';
    }

    // Load PDF into memory
    const arrayBuffer = await file.arrayBuffer();
    const pdfDocument = await window.pdfjsLib.getDocument({ data: arrayBuffer }).promise;

    const fields: Array<{page: number, name: string, type: string}> = [];
    // Iterate every page
    for (let i = 1; i <= pdfDocument.numPages; i++) {
      const page = await pdfDocument.getPage(i);
      // Get all display annotations (including form widgets)
      const annotations = await page.getAnnotations({ intent: 'display' });

      // Filter for form-field widgets
      annotations.filter((a: any) => a.subtype === 'Widget')
        .forEach((a: any) => fields.push({
          page: i,
          name: a.fieldName,
          type: a.fieldType
        }));
    }

    // Generate JSON schema from field names
    const jsonSchema: { [key: string]: string } = {};
    fields.forEach(field => {
      // Keep field names exactly as they appear in the PDF
      if (field.name) {
        jsonSchema[field.name] = "";
      }
    });

    return {
      fields,
      jsonSchema: JSON.stringify(jsonSchema, null, 2)
    };
  }, []);

  const handleValidate = useCallback(async () => {
    if (!auth.isAuthenticated || !uploadedFile || !currentAccount) return;

    setIsValidating(true);
    setValidationResult(null);
    setFieldCount(null);

    try {
      // Extract PDF fields
      const file = await convertBase64ToFile(uploadedFile);
      const { fields } = await extractPdfFields(file);
      setFieldCount(fields.length);

      if (fields.length === 0) {
        setValidationResult({
          success: false,
          message: 'This PDF document does not have fillable fields, and cannot be populated.'
        });
        setIsValidating(false);
        return;
      }

      // No backend call, just show success
      setValidationResult({
        success: true,
        message: 'Template validated successfully!'
      });
    } catch (error) {
      setValidationResult({
        success: false,
        message: 'Validation failed. Please check your PDF and try again.'
      });
    } finally {
      setIsValidating(false);
    }
  }, [auth.isAuthenticated, uploadedFile, currentAccount, extractPdfFields, convertBase64ToFile]);

  const handleUpload = useCallback(async () => {
    if (!auth.isAuthenticated || !uploadedFile || !validationResult?.success || !currentAccount || !templateName.trim()) return;

    setIsUploading(true);
    setUploadResult(null);

    try {
      // Extract PDF fields to get JSON schema
      const file = await convertBase64ToFile(uploadedFile);
      const { jsonSchema } = await extractPdfFields(file);
      
      // Upload template to S3 and create task definition
      await api.uploadTemplate(uploadedFile, currentAccount, templateName.trim(), jsonSchema);
      //todo - handle failures for uploadtemplate

      setUploadResult({
        success: true,
        message: 'Template uploaded successfully!'
      });
      
      // Close modal after a brief delay to show success message
      setTimeout(() => {
        onTemplateAdded();
        onClose();
        // Reset state
        setUploadedFile(null);
        setValidationResult(null);
        setUploadResult(null);
        setTemplateName('');
        setIsValidating(false);
        setIsUploading(false);
      }, 1500);
    } catch (error) {
      setUploadResult({
        success: false,
        message: 'Upload failed. Please try again.'
      });
      setIsUploading(false);
    }
  }, [auth.isAuthenticated, uploadedFile, validationResult, currentAccount, templateName, onTemplateAdded, onClose, extractPdfFields]);

  const handleClose = useCallback(() => {
    if (!isValidating && !isUploading) {
      onClose();
      // Reset state
      setUploadedFile(null);
      setValidationResult(null);
      setUploadResult(null);
      setTemplateName('');
    }
  }, [isValidating, isUploading, onClose]);

  if (!isVisible) return null;
  if (auth.isLoading) return null;
  if (!auth.isAuthenticated) return null;
  if (!currentAccount) return null;

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
      role="dialog"
      aria-labelledby="modal-title"
    >
      <div className="fixed inset-0 bg-black/50" onClick={handleClose} />
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto relative z-10">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 id="modal-title" className="text-lg font-medium text-gray-900">Add New Template</h2>
          <button
            className="w-8 h-8 bg-gray-100 text-gray-600 rounded-full flex items-center justify-center hover:bg-gray-200 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={handleClose}
            disabled={isValidating || isUploading}
            aria-label="Close Add New Template dialog"
          >
            ×
          </button>
        </div>
        <div className="p-6 relative">
          {/* File Upload Area */}
          {!uploadedFile && (
            <div className="mb-6">
              <div
                className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center transition-colors duration-200 hover:border-blue-400 hover:bg-blue-50 cursor-pointer"
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onClick={handleClick}
                tabIndex={0}
                role="button"
                aria-label="Upload PDF template"
              >
                <div className="flex flex-col items-center">
                  <div className="text-4xl mb-4">📄</div>
                  <p className="text-lg font-medium text-gray-900 mb-2">
                    Drag & drop your PDF template here
                  </p>
                  <p className="text-sm text-gray-600 mb-2">
                    or click to select a PDF file
                  </p>
                  <p className="text-xs text-gray-500">
                    Only fillable PDF forms are supported
                  </p>
                </div>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="application/pdf"
                onChange={handleFileInputChange}
                className="hidden"
              />
            </div>
          )}
          {/* File Preview */}
          {uploadedFile && (
            <div className="mb-6">
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
                <div className="flex items-center space-x-3">
                  <div className="text-2xl">📄</div>
                  <div>
                    <div className="font-medium text-gray-900">{uploadedFile.name}</div>
                    <div className="text-sm text-gray-500">PDF Template</div>
                  </div>
                </div>
                <button
                  className="w-6 h-6 bg-red-100 text-red-600 rounded-full flex items-center justify-center hover:bg-red-200 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={() => setUploadedFile(null)}
                  disabled={isValidating || isUploading}
                  aria-label="Remove uploaded file"
                >
                  ×
                </button>
              </div>
              {/* Validation Section */}
              {!validationResult && (
                <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-sm text-blue-800 mb-4">
                    Ready to validate your template. This will extract form fields and verify compatibility.
                  </p>
                  <button
                    className="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 bg-blue-600 text-white hover:bg-blue-700 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    onClick={handleValidate}
                    disabled={isValidating}
                  >
                    {isValidating ? 'Validating...' : 'Validate Template'}
                  </button>
                </div>
              )}
              {/* Validation Result */}
              {validationResult && (
                <div className={`mb-6 p-4 rounded-lg border flex items-center space-x-3 ${validationResult.success ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center text-white font-bold ${validationResult.success ? 'bg-green-600' : 'bg-red-600'}`}>
                    {validationResult.success ? '✓' : '✗'}
                  </div>
                  <div className={`text-sm ${validationResult.success ? 'text-green-800' : 'text-red-800'}`}>
                    {validationResult.message}
                  </div>
                </div>
              )}
              {/* Upload Section */}
              {validationResult?.success && !uploadResult && (
                <div className="mb-6">
                  <div className="mb-4">
                    <label htmlFor="template-name" className="block text-sm font-medium text-gray-700 mb-1">
                      Template Name:
                    </label>
                    <input
                      id="template-name"
                      type="text"
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      value={templateName}
                      onChange={(e) => setTemplateName(e.target.value)}
                      placeholder="Enter template name..."
                      required
                      autoFocus
                    />
                  </div>
                  <p className="text-sm text-gray-600 mb-4">
                    Template is ready to be added to your library.
                  </p>
                  <button
                    className="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 bg-blue-600 text-white hover:bg-blue-700 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    onClick={handleUpload}
                    disabled={isUploading || !templateName.trim()}
                  >
                    {isUploading ? 'Uploading...' : 'Add Template'}
                  </button>
                </div>
              )}
              {/* Upload Result */}
              {uploadResult && (
                <div className={`mb-6 p-4 rounded-lg border flex items-center space-x-3 ${uploadResult.success ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center text-white font-bold ${uploadResult.success ? 'bg-green-600' : 'bg-red-600'}`}>
                    {uploadResult.success ? '✓' : '✗'}
                  </div>
                  <div className={`text-sm ${uploadResult.success ? 'text-green-800' : 'text-red-800'}`}>
                    {uploadResult.message}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
        {/* Loading Overlay for Validation */}
        {isValidating && (
          <div className="absolute inset-0 bg-white/80 flex items-center justify-center z-10">
            <LoadingOverlay
              isVisible={true}
              currentStage={1}
              totalStages={2}
              stageNames={['Extracting Fields', 'Validating Template']}
            />
          </div>
        )}
        {/* Loading Overlay for Upload */}
        <LoadingOverlay
          isVisible={isUploading}
          currentStage={1}
          totalStages={2}
          stageNames={['Processing', 'Adding Template']}
        />
      </div>
    </div>
  );
}; 